import { FontNames } from '@jerry-serverless/ui-fonts/lib/const.FontsCSS';

import { gridTheme } from './gridTheme';
import gridThemeToStyledThemeProviderTheme from './gridThemeToStyledThemeProviderTheme';
import type { JuiTheme } from './types.juiTheme';
import { Breakpoint as breakpoints, zIndex } from './const.theme';

/**
 * Convert hex string to 8bit RGB component integers
 */
const parseHex = (hex: string): Array<number> => {
  const r = parseInt(hex.substr(1, 2), 16);
  const g = parseInt(hex.substr(3, 2), 16);
  const b = parseInt(hex.substr(5, 2), 16);

  return [r, g, b];
};

/**
 * Converts a hex color to comma separated RGB values.
 * Example: '#0080C0' -> '0, 128, 192'
 */
export const hexToRGB = (hex: string): string => {
  const [r, g, b] = parseHex(hex);
  return `${r}, ${g}, ${b}`;
};

export const legacyColors = {
  black: '#0D202F',
  black60: `rgba(${hexToRGB('#0D202F')}, 0.6)`,
  black40: `rgba(${hexToRGB('#0D202F')}, 0.4)`,
  white: '#fff',
  grayXd: '#3B4F60',
  grayXd20: `rgba(${hexToRGB('#3B4F60')}, 0.2)`,
  grayD: '#6B7A86',
  grayD60: `rgba(${hexToRGB('#6B7A86')}, 0.6)`,
  grayPanelShadow: '#CCC',
  grayPageBackground: '#F1F4F6',
  grayLightBorder: '#D7DBDD',
  grayExtraLightBorder: '#E0E5E8',
  grayUltraLightBorder: '#E4E7E9',
  grayLightBackground: '#F1F4F6',
  svgFill: '#0D202F',
};

export const juiTheme: JuiTheme = {
  color: {
    // Primary
    PrimaryBG: '#FFF8FA',
    PrimaryDark: '#E81656',
    PrimaryDarkmode: '#FFADC6',
    PrimaryDefault: '#FF3975',
    PrimaryLight: '#FFE8EF',

    // deprecated, Cover ui-components, do not use these
    primaryXd: '#FF3975',
    primaryD: '#FF3975',

    // Secondary
    SecondaryBG: '#E5EBFE',
    SecondaryDark: '#032596',
    SecondaryDarkmode: '#9BB2FD',
    SecondaryDefault: '#063EF9',
    SecondaryLight: '#F2F5FF',

    // Success
    SuccessBG: '#E5FFF2',
    SuccessDark: '#00994D',
    SuccessDarkmode: '#4CFFA6',
    SuccessDefault: '#00CC67',
    SuccessLight: '#F2FFF9',

    // Warning
    WarningBG: '#FEFEE5',
    WarningDark: '#6A5300',
    WarningDarkmode: '#F2F294',
    WarningDefault: '#EEEE2C',
    WarningLight: '#FFFFF2',

    // Error
    ErrorBG: '#FFE5F2',
    ErrorDark: '#98014C',
    ErrorDarkmode: '#FF5DA1',
    ErrorDefault: '#E40173',
    ErrorLight: '#FFF2F9',

    // Grayscale
    GrayscaleAsh: '#232A38',
    GrayscaleBackground: '#F8F9FA',
    GrayscaleBody: '#4B5466',
    GrayscaleInput: '#EFF3F7',
    GrayscaleLabel: '#6E7E91',
    GrayscaleLine: '#D9E0E9',
    GrayscaleOffBlack: '#141D2B',
    GrayscaleOffWhite: '#FCFCFC',
    GrayscalePlaceholder: '#A0AFBD',
    GrayLight: '#6B7A86',

    // Chili
    Chili100: '#FFE6E6',
    Chili200: '#FF9999',
    Chili300: '#FF6666',
    Chili400: '#FF4C4D',
    Chili500: '#FF0000',
    Chili50: '#FFF2F2',
    Chili600: '#E60000',
    Chili700: '#CC0000',
    Chili800: '#990000',
    Chili900: '#660000',
    // Clover
    Clover100: '#E6FFF2',
    Clover200: '#99FFCC',
    Clover300: '#66FFB3',
    Clover400: '#4CFFA6',
    Clover500: '#00FE80',
    Clover50: '#F2FFF9',
    Clover600: '#00E674',
    Clover700: '#00CC67',
    Clover800: '#00994D',
    Clover900: '#006633',
    // Cool
    Cool100: '#F7F7FC',
    Cool200: '#EFF0F6',
    Cool300: '#D9DBE9',
    Cool400: '#A0A3BD',
    Cool500: '#6E7191',
    Cool50: '#FCFCFC',
    Cool600: '#6E7191',
    Cool700: '#4E4B66',
    Cool800: '#262338',
    Cool900: '#14142A',
    // Cyan
    Cyan100: '#E6FFFF',
    Cyan200: '#99FFFF',
    Cyan300: '#66FFFF',
    Cyan400: '#4CFFFF',
    Cyan500: '#00FFFF',
    Cyan50: '#F2FFFF',
    Cyan600: '#00E6E5',
    Cyan700: '#00CCCC',
    Cyan800: '#009999',
    Cyan900: '#006666',
    // Dragon
    Dragon100: '#FFE5F2',
    Dragon200: '#FF99CC',
    Dragon300: '#FE67B2',
    Dragon400: '#FF5DA1',
    Dragon500: '#FE017F',
    Dragon50: '#FFF2F9',
    Dragon600: '#E40173',
    Dragon700: '#CB0166',
    Dragon800: '#98014C',
    Dragon900: '#660033',
    // Emerald
    Emerald100: '#E6FFF9',
    Emerald200: '#99FFE9',
    Emerald300: '#66FFDD',
    Emerald400: '#4CFFD8',
    Emerald500: '#00FFC7',
    Emerald50: '#F2FFFC',
    Emerald600: '#00E6B3',
    Emerald700: '#00CC9F',
    Emerald800: '#009977',
    Emerald900: '#006650',

    // Gold
    Gold100: '#FBF3E2',
    Gold200: '#F3D9A5',
    Gold300: '#EEC677',
    Gold400: '#EBBD61',
    Gold500: '#E2A21F',
    Gold50: '#FEFAF1',
    Gold600: '#CB911A',
    Gold700: '#B58117',
    Gold800: '#886011',
    Gold900: '#5A400C',

    // Lavender
    Lavender100: '#EEE5FE',
    Lavender200: '#C19CFC',
    Lavender300: '#A26BFA',
    Lavender400: '#9252FA',
    Lavender500: '#6308F7',
    Lavender50: '#F7F2FF',
    Lavender600: '#5907DE',
    Lavender700: '#4F06C6',
    Lavender800: '#3C0594',
    Lavender900: '#280363',

    // Lemon
    Lemon100: '#FEFEE5',
    Lemon200: '#F3F3B2',
    Lemon300: '#F2F294',
    Lemon400: '#F1F169',
    Lemon500: '#EEEE2C',
    Lemon50: '#FFFFF2',
    Lemon600: '#E9E003',
    Lemon700: '#DCC80F',
    Lemon800: '#A37F00',
    Lemon900: '#6A5300',
    // Lilac
    Lilac100: '#F6E5FE',
    Lilac200: '#DF9BFD',
    Lilac300: '#CF69FC',
    Lilac400: '#C750FC',
    Lilac500: '#AF05FA',
    Lilac50: '#FBF2FF',
    Lilac600: '#9D04E1',
    Lilac700: '#8C04C8',
    Lilac800: '#690396',
    Lilac900: '#460264',

    // Lime
    Lime100: '#EEFFE5',
    Lime200: '#D0F9BC',
    Lime300: '#B1F68E',
    Lime400: '#88EA57',
    Lime500: '#57D916',
    Lime50: '#F6FFF2',
    Lime600: '#44CB00',
    Lime700: '#3DB600',
    Lime800: '#339801',
    Lime900: '#226501',
    // Magenta
    Magenta100: '#FFE6FA',
    Magenta200: '#FF99E9',
    Magenta300: '#FF66DF',
    Magenta400: '#FF4CD9',
    Magenta500: '#FF00C9',
    Magenta50: '#FFF2FC',
    Magenta600: '#E600B5',
    Magenta700: '#CC00A1',
    Magenta800: '#990079',
    Magenta900: '#660050',
    // Ocean
    Ocean100: '#E6F8FF',
    Ocean200: '#99E3FF',
    Ocean300: '#77DAFF',
    Ocean400: '#4CCFFF',
    Ocean500: '#00BAFF',
    Ocean50: '#F2FCFF',
    Ocean600: '#00A7E6',
    Ocean700: '#0095CC',
    Ocean800: '#007099',
    Ocean900: '#004A66',

    // Sky
    Sky100: '#E5EBFE',
    Sky200: '#9BB2FD',
    Sky300: '#7897FF',
    Sky400: '#5078FB',
    Sky500: '#063EF9',
    Sky50: '#F2F5FF',
    Sky600: '#0538E0',
    Sky700: '#0532C7',
    Sky800: '#032596',
    Sky900: '#021964',
    // Tiger
    Tiger100: '#FFF1E6',
    Tiger200: '#FFC899',
    Tiger300: '#FFAC66',
    Tiger400: '#FFAC66',
    Tiger500: '#FF7500',
    Tiger50: '#FFF8F2',
    Tiger600: '#E66900',
    Tiger700: '#CC5E00',
    Tiger800: '#994600',
    Tiger900: '#662F00',
    // Warm
    Warm100: '#EEEDED',
    Warm200: '#CDCBCB',
    Warm300: '#B5B0B0',
    Warm400: '#A8A3A3',
    Warm500: '#837C7C',
    Warm50: '#FAFAFA',
    Warm600: '#767070',
    Warm700: '#5C5757',
    Warm800: '#413E3E',
    Warm900: '#272525',

    // Transparent
    TransparentDarkOverlay10: 'rgba(20, 20, 43, 0.1)',
    TransparentDarkOverlay25: 'rgba(20, 20, 43, 0.25)',
    TransparentDarkOverlay40: 'rgba(20, 20, 43, 0.4)',
    TransparentDarkOverlay65: 'rgba(20, 20, 43, 0.65)',
    TransparentDarkOverlay75: 'rgba(20, 20, 43, 0.75)',
    TransparentDarkOverlay95: 'rgba(20, 20, 43, 0.95)',
    TransparentLightOverlay10: 'rgba(239, 240, 246, 0.1)',
    TransparentLightOverlay25: 'rgba(239, 240, 246, 0.25)',
    TransparentLightOverlay40: 'rgba(239, 240, 246, 0.4)',
    TransparentLightOverlay65: 'rgba(239, 240, 246, 0.65)',
    TransparentLightOverlay75: 'rgba(239, 240, 246, 0.75)',
    TransparentLightOverlay95: 'rgba(239, 240, 246, 0.95)',

    ...legacyColors,
  },

  shadow: {
    large: '0px 24px 56px rgba(0, 0, 0, 0.1)',
    medium: '0px 12px 16px rgba(0, 0, 0, 0.1)',
    small: '0px 4px 6px rgba(0, 0, 0, 0.1)',
  },

  font: {
    circular: `${FontNames.circular}, ${FontNames.circularFallback}, Arial, sans-serif`,
    inter: `${FontNames.inter}, ${FontNames.interFallback}, PingFangSC-Medium, Arial, sans-serif`,
    signature: `${FontNames.signature}, Arial, sans-serif`,
    icons: `${FontNames.icons}`,
  },

  breakpoints,
  zIndex,
};

export const defaultTheme = { ...juiTheme, ...gridThemeToStyledThemeProviderTheme(gridTheme) };
